import apiService from './apiService';
import axios from 'axios';

class ShopifyBulkOperationsService {
  constructor(shop, accessToken) {
    console.log('Initializing ShopifyBulkOperationsService with shop:', shop);
    this.shop = shop;
    this.accessToken = accessToken;

    // Field mappings for Shopify API
    this.fieldMapping = {
      title: 'title',
      description: 'descriptionHtml',
      handle: 'handle',
      vendor: 'vendor',
      product_type: 'productType',
      productType: 'productType',
      tags: 'tags',
      status: 'status',
      images_alt_text: 'featuredImage.altText',
      variant_images_alt_text: 'media.alt',
      seo_title: 'seo.title',
      seo_description: 'seo.description',
      option1_name: 'options[0].name',
      option2_name: 'options[1].name',
      option3_name: 'options[2].name'
    };
  }

  async makeGraphQLRequest(query, variables = null, shopOverride = null, accessTokenOverride = null) {
    let shop = shopOverride || this.shop;
    let accessToken = accessTokenOverride || this.accessToken;

    // Always try to use the latest token from ShopContext (avoids stale token race conditions)
    if (!shopOverride && !accessTokenOverride && typeof window !== 'undefined' && window.__currentShopData) {
      const liveShop = window.__currentShopData;
      if (liveShop.shop === shop && liveShop.accessToken) {
        // Check if CCG token is near expiry and wait for refresh
        if (liveShop.tokenType === 'ccg_custom_app' && liveShop.tokenExpiresAt) {
          const bufferMs = 15 * 60 * 1000; // Same 15-min buffer as ShopContext
          if (Date.now() > (liveShop.tokenExpiresAt - bufferMs)) {
            console.log('⏳ CCG token near expiry, waiting for background refresh...');
            // Poll for up to 15 seconds (30 x 500ms) for the token to be refreshed
            for (let i = 0; i < 30; i++) {
              await new Promise(r => setTimeout(r, 500));
              const refreshedShop = window.__currentShopData;
              if (refreshedShop?.tokenExpiresAt && Date.now() <= (refreshedShop.tokenExpiresAt - bufferMs)) {
                console.log('✅ CCG token refreshed, proceeding with request');
                accessToken = refreshedShop.accessToken;
                break;
              }
            }
            // Final check - use whatever we have
            const finalShop = window.__currentShopData;
            if (finalShop?.accessToken) {
              accessToken = finalShop.accessToken;
            }
          } else {
            // Token is still valid, use the live token (may be newer than constructor token)
            accessToken = liveShop.accessToken;
          }
        } else {
          // Non-CCG token, just use the latest
          accessToken = liveShop.accessToken;
        }
        // Also update instance properties so retries within the same service use fresh tokens
        this.accessToken = accessToken;
      }
    }

    if (!shop || !accessToken) {
      throw new Error('Shop and access token are required');
    }

    console.log('Sending GraphQL request:', { query, variables });

    try {
      // Use the apiService to make the GraphQL request
      const response = await apiService.graphql(query, variables, shop, accessToken);

      if (response.data.errors) {
        console.error('GraphQL errors:', response.data.errors);
        throw new Error(response.data.errors[0].message);
      }

      return response.data.data;
    } catch (error) {
      // On 500 error, try once more with the absolute latest token
      // (the refresh may have completed while this request was in-flight)
      if (error.response?.status === 500 && typeof window !== 'undefined' && window.__currentShopData) {
        const retryShop = window.__currentShopData;
        if (retryShop.shop === shop && retryShop.accessToken && retryShop.accessToken !== accessToken) {
          console.log('🔄 Got 500 error, retrying with refreshed token...');
          this.accessToken = retryShop.accessToken;
          try {
            const retryResponse = await apiService.graphql(query, variables, shop, retryShop.accessToken);
            if (retryResponse.data.errors) {
              throw new Error(retryResponse.data.errors[0].message);
            }
            return retryResponse.data.data;
          } catch (retryError) {
            console.error('Retry also failed:', retryError.message);
            // Fall through to original error handling
          }
        }
      }

      console.error('GraphQL request failed:', {
        error,
        query,
        variables,
        response: error.response?.data
      });
      throw error;
    }
  }

  setShopCredentials(shop, accessToken) {
    this.shop = shop;
    this.accessToken = accessToken;
  }

  async startBulkProductsQuery(query, retryCount = 0) {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_BASE = 3000; // 3 seconds base, exponential backoff

    try {
      // First check if there's already an operation running
      let currentOperation = null;
      try {
        currentOperation = await this.checkBulkOperationStatus();
      } catch (statusError) {
        // If checking status fails (e.g. 500 from expired token), wait and retry
        console.warn('Failed to check bulk operation status, will retry:', statusError.message);
        if (retryCount < MAX_RETRIES) {
          const delay = RETRY_DELAY_BASE * Math.pow(2, retryCount);
          console.log(`Waiting ${delay}ms before retry ${retryCount + 1}/${MAX_RETRIES}...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.startBulkProductsQuery(query, retryCount + 1);
        }
        throw statusError;
      }

      // If there's an operation running, wait for it to complete or fail
      if (currentOperation && ['CREATED', 'RUNNING'].includes(currentOperation.status)) {
        console.log(`Existing bulk operation found (status: ${currentOperation.status}), waiting for completion...`);
        try {
          const completedOp = await this.pollOperationStatus(60, 2000); // Wait up to 2 minutes
          console.log('Previous operation completed:', completedOp?.status);
        } catch (pollError) {
          console.log('Previous operation failed or timed out, attempting to cancel...');
          try {
            await this.cancelCurrentBulkOperation();
            // Wait a moment for the cancellation to take effect
            await new Promise(resolve => setTimeout(resolve, 2000));
          } catch (cancelError) {
            console.warn('Failed to cancel previous operation:', cancelError.message);
          }
        }
      }

      console.log('Starting bulk operation with query:', query);

      const response = await this.makeGraphQLRequest(`
        mutation {
          bulkOperationRunQuery(
            query: """${query}"""
          ) {
            bulkOperation {
              id
              status
            }
            userErrors {
              field
              message
            }
          }
        }
      `);

      console.log('Bulk operation response:', JSON.stringify(response, null, 2));

      if (!response?.bulkOperationRunQuery) {
        throw new Error('Failed to start bulk operation');
      }

      const result = response.bulkOperationRunQuery;

      if (result.userErrors && result.userErrors.length > 0) {
        const errorMessages = result.userErrors.map(error => error.message).join(', ');
        console.error('Bulk operation user errors:', result.userErrors);

        // Handle "already in progress" by waiting and retrying
        if (errorMessages.includes('already in progress') && retryCount < MAX_RETRIES) {
          const delay = RETRY_DELAY_BASE * Math.pow(2, retryCount);
          console.log(`Operation already in progress, waiting ${delay}ms before retry ${retryCount + 1}/${MAX_RETRIES}...`);
          await new Promise(resolve => setTimeout(resolve, delay));

          // Poll for the existing operation to finish
          try {
            await this.pollOperationStatus(30, 2000);
          } catch (pollError) {
            console.warn('Polling existing operation failed:', pollError.message);
          }

          return this.startBulkProductsQuery(query, retryCount + 1);
        }

        throw new Error(errorMessages);
      }

      if (!result.bulkOperation) {
        throw new Error('No bulk operation returned from query');
      }

      return result.bulkOperation;
    } catch (error) {
      console.error('Failed to start bulk operation:', error);

      // Retry on 500 errors (typically token refresh issues)
      if (error.response?.status === 500 && retryCount < MAX_RETRIES) {
        const delay = RETRY_DELAY_BASE * Math.pow(2, retryCount);
        console.log(`Server error (500), waiting ${delay}ms before retry ${retryCount + 1}/${MAX_RETRIES}...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.startBulkProductsQuery(query, retryCount + 1);
      }

      throw error;
    }
  }

  async cancelCurrentBulkOperation() {
    try {
      const response = await this.makeGraphQLRequest(`
        mutation {
          bulkOperationCancel {
            bulkOperation {
              id
              status
            }
            userErrors {
              field
              message
            }
          }
        }
      `);
      console.log('Bulk operation cancel response:', response);
      return response?.bulkOperationCancel?.bulkOperation;
    } catch (error) {
      console.error('Error cancelling bulk operation:', error);
      throw error;
    }
  }

  async checkBulkOperationStatus() {
    try {
      const response = await this.makeGraphQLRequest(`
        query {
          currentBulkOperation {
            id
            status
            errorCode
            createdAt
            completedAt
            objectCount
            fileSize
            url
            partialDataUrl
          }
        }
      `);

      return response?.currentBulkOperation;
    } catch (error) {
      console.error('Error checking bulk operation status:', error);
      throw error;
    }
  }

  async pollOperationStatus(maxAttempts = 30, pollInterval = 2000) {
    console.log('Polling operation status...');
    let attempts = 0;

    while (attempts < maxAttempts) {
      attempts++;
      console.log(`Poll attempt ${attempts}/${maxAttempts}`);

      try {
        const operation = await this.checkBulkOperationStatus();

        if (!operation) {
          console.log('No operation found, assuming completed');
          return null;
        }

        console.log('Current operation status:', operation.status);

        if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(operation.status)) {
          return operation;
        }

        if (operation.status === 'COMPLETED' && (operation.url || operation.partialDataUrl)) {
          return operation;
        }

        if (operation.status === 'COMPLETED' && !operation.url && !operation.partialDataUrl) {
          throw new Error('Operation completed but no URL provided');
        }
      } catch (pollErr) {
        console.warn('Error during pollOperationStatus:', pollErr.message);
        // Continue loop to allow it to recover
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
  }

  async pollBulkOperation(operationId, maxAttempts = 30, interval = 2000) {
    console.log('Starting to poll bulk operation:', operationId);
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await this.makeGraphQLRequest(`
          query {
            currentBulkOperation {
              id
              status
              errorCode
              createdAt
              completedAt
              objectCount
              fileSize
              url
              partialDataUrl
            }
          }
        `);

        console.log('Poll response:', response);

        if (!response.currentBulkOperation) {
          console.error('No current bulk operation found');
          return null;
        }

        const operation = response.currentBulkOperation;
        const status = {
          status: operation.status,
          objectCount: operation.objectCount,
          fileSize: operation.fileSize
        };

        console.log('Bulk operation status:', status);

        if (operation.status === 'COMPLETED') {
          console.log('Bulk operation completed successfully');
          const url = operation.url;
          if (!url) {
            console.warn('No URL in completed operation, trying to use partialDataUrl');
            if (operation.partialDataUrl) {
              return await this.downloadBulkOperationData(operation.partialDataUrl);
            }
            throw new Error('No URL in completed operation');
          }
          return await this.downloadBulkOperationData(url);
        } else if (operation.status === 'FAILED') {
          const errorCode = operation.errorCode || 'UNKNOWN_ERROR';
          console.error(`Bulk operation failed: ${errorCode}`);

          // Provide more detailed error information based on error code
          if (errorCode === 'ACCESS_DENIED') {
            throw new Error(`Bulk operation failed: ACCESS_DENIED - The app doesn't have permission to perform this operation for shop ${this.shop}. Please check your app's API access scopes.`);
          } else {
            throw new Error(`Bulk operation failed: ${errorCode}`);
          }
        } else if (operation.status === 'CANCELED') {
          console.error('Bulk operation was canceled');
          throw new Error('Bulk operation was canceled');
        }

        // If we get here, the operation is still in progress
        attempts++;
        await new Promise(resolve => setTimeout(resolve, interval));
      } catch (error) {
        console.warn('Error polling bulk operation during loop:', error.message);
        // Do not throw! Just increment attempts and try again.
        attempts++;
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }

    throw new Error(`Bulk operation polling timed out after ${maxAttempts} attempts`);
  }

  async downloadBulkOperationData(url) {
    try {
      console.log('Downloading bulk operation data from:', url);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to download bulk operation data: ${response.status} ${response.statusText}`);
      }

      const data = await response.text();
      console.log('Downloaded data size:', data.length);
      return data;
    } catch (error) {
      console.error('Error downloading bulk operation data:', error);
      throw error;
    }
  }

  processProductsData(jsonlData) {
    try {
      if (!jsonlData || jsonlData.trim() === '') {
        console.log('No data to process');
        return [];
      }

      const lines = jsonlData.trim().split('\n');
      const products = new Map(); // Use a map to group product data

      console.log('Processing JSONL data, total lines:', lines.length);

      for (const line of lines) {
        try {
          const data = JSON.parse(line);

          // Check if this is a product node
          if (data.id && data.id.includes('gid://shopify/Product/')) {
            // Filter out system metafields and only keep product-specific ones
            if (data.metafields && data.metafields.edges) {
              console.log('Before filtering metafields:', data.metafields.edges.length);

              // Extract all metafields from edges
              const allMetafields = data.metafields.edges.map(edge => edge.node);

              // Log all shopify metafields that will be filtered out
              // Prüfe auf "shopify" als Namespace (ohne Punkt)
              const shopifyMetafields = allMetafields.filter(metafield =>
                metafield.namespace && (metafield.namespace === "shopify" || metafield.namespace.startsWith("shopify."))
              );

              if (shopifyMetafields.length > 0) {
                console.log(`Found ${shopifyMetafields.length} protected Shopify metafields to filter out:`);
                shopifyMetafields.forEach(metafield => {
                  console.log(`Filtering out protected metafield: ${metafield.namespace}.${metafield.key}`);
                });
              }

              // Apply the filtering
              data.metafields = allMetafields.filter(metafield =>
                metafield.id &&
                !metafield.key.startsWith("gid://shopify/Metafield/") &&
                metafield.key !== "null" &&
                !metafield.key.includes("custom.custom.") &&
                // Filter out protected Shopify metafields - both "shopify" namespace and "shopify." prefixed namespaces
                !(metafield.namespace && (metafield.namespace === "shopify" || metafield.namespace.startsWith("shopify.")))
              );

              console.log('After filtering metafields:', data.metafields.length);
              console.log(`Removed ${allMetafields.length - data.metafields.length} metafields, including ${shopifyMetafields.length} protected Shopify metafields`);
            }

            products.set(data.id, {
              ...data,
              variants: [],
              metafields: [],
              originalMetafields: data.metafields || [], // Store original metafields
              images: []
            });
          }
          // Check if this is a variant node
          else if (data.id && data.id.includes('gid://shopify/ProductVariant/') && data.__parentId) {
            const product = products.get(data.__parentId);
            if (product) {
              product.variants.push(data);
            }
          }
          // Check if this is a metafield
          else if (data.id && data.id.includes('gid://shopify/Metafield/') && data.__parentId) {
            const product = products.get(data.__parentId);
            if (product) {
              product.metafields.push(data);
            }
          }
          // Check if this is an image (supports both ProductImage and MediaImage GIDs for API 2025-10 compatibility)
          else if (data.id && (data.id.includes('gid://shopify/ProductImage/') || data.id.includes('gid://shopify/MediaImage/')) && data.__parentId) {
            const product = products.get(data.__parentId);
            if (product) {
              product.images.push(data);
            }
          }
        } catch (error) {
          console.error('Error processing JSONL line:', error);
          // Continue with next line
        }
      }

      // Convert map to array and process each product
      const processedProducts = Array.from(products.values()).map(product => {
        // Process images
        if (product.images && product.images.length > 0) {
          // Sort images by position
          product.images.sort((a, b) => (a.position || 0) - (b.position || 0));

          // Set featured image if not already set
          if (!product.featuredImage && product.images.length > 0) {
            product.featuredImage = product.images[0];
          }
        }

        // Process variants
        if (product.variants && product.variants.length > 0) {
          // Sort variants by position
          product.variants.sort((a, b) => (a.position || 0) - (b.position || 0));

          // Process variant images
          product.variants.forEach(variant => {
            if (variant.image && variant.image.id) {
              // Find the full image data from product images
              const fullImage = product.images.find(img => img.id === variant.image.id);
              if (fullImage) {
                variant.image = fullImage;
              }
            }
          });
        }

        return product;
      });

      console.log(`Processed ${processedProducts.length} products`);
      return processedProducts;
    } catch (error) {
      console.error('Error processing products data:', error);
      throw error;
    }
  }

  async getMetafieldDefinitions() {
    try {
      const response = await this.makeGraphQLRequest(`
        query {
          metafieldDefinitions(
            first: 250,
            ownerType: PRODUCT
          ) {
            nodes {
              id
              name
              namespace
              key
              description
              type {
                name
              }
              validations {
                name
                value
              }
              ownerType
            }
          }
        }
      `);

      if (!response?.metafieldDefinitions?.nodes) {
        console.error('No metafield definitions found:', response);
        return [];
      }

      const definitions = response.metafieldDefinitions.nodes;
      console.log('Found metafield definitions:', definitions);
      return definitions;
    } catch (error) {
      console.error('Error fetching metafield definitions:', error);
      return [];
    }
  }

  async enrichProductsWithMetafieldDefinitions(products) {
    try {
      // Get all metafield definitions for products
      const metafieldDefinitions = await this.getMetafieldDefinitions();

      if (!metafieldDefinitions || metafieldDefinitions.length === 0) {
        console.log('No metafield definitions found, returning original products');
        return products;
      }

      console.log(`Enriching ${products.length} products with ${metafieldDefinitions.length} metafield definitions`);

      // Process each product
      return products.map(product => {
        // Create a map of existing metafields by namespace.key
        const existingMetafields = {};
        if (product.metafields && Array.isArray(product.metafields)) {
          product.metafields.forEach(metafield => {
            const key = `${metafield.namespace}.${metafield.key}`;
            existingMetafields[key] = metafield;
          });
        }

        // Create a new array with all metafield definitions, using existing values where available
        // Filter out any metafield definitions with namespace starting with "shopify."
        const enrichedMetafields = metafieldDefinitions
          .filter(definition => !definition.namespace.startsWith('shopify.'))
          .map(definition => {
            const key = `${definition.namespace}.${definition.key}`;

            // If we have an existing metafield with this namespace and key, use its value
            if (existingMetafields[key]) {
              return existingMetafields[key];
            }

            // Otherwise create a new metafield with empty value
            return {
              id: null, // No ID for new metafields
              namespace: definition.namespace,
              key: definition.key,
              value: null,
              type: definition.type.name,
              name: definition.name,
              description: definition.description
            };
          });

        // Log how many protected metafields were filtered out
        const filteredCount = metafieldDefinitions.filter(def => def.namespace.startsWith('shopify.')).length;
        if (filteredCount > 0) {
          console.log(`Filtered out ${filteredCount} protected Shopify metafield definitions`);
        }

        // Return the product with enriched metafields
        return {
          ...product,
          metafields: enrichedMetafields,
          // Keep the original metafields for reference
          originalMetafields: product.metafields || []
        };
      });
    } catch (error) {
      console.error('Error enriching products with metafield definitions:', error);
      return products;
    }
  }

  static buildFullProductQueryForIds(productIds) {
    // If productIds is a string, convert it to an array
    const ids = Array.isArray(productIds) ? productIds : [productIds];

    // Build the ID filter
    const idFilter = ids.map(id => {
      // Strip any existing gid:// prefix to ensure consistent formatting
      const cleanId = id.replace('gid://shopify/Product/', '');
      return `id:${cleanId}`;
    }).join(' OR ');

    console.log('Querying products with filter:', idFilter);

    return `
      {
        products(query: "${idFilter}", first: 250) {
          edges {
            node {
              id
              title
              handle
              descriptionHtml
              vendor
              productType
              status
              tags
              publishedAt
              createdAt
              updatedAt
              onlineStoreUrl
              hasOnlyDefaultVariant
              requiresSellingPlan
              seo {
                title
                description
              }
              options {
                id
                name
                position
                values
              }
              priceRangeV2 {
                maxVariantPrice {
                  amount
                  currencyCode
                }
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              variants(first: 1000) {
                edges {
                  node {
                    id
                    title
                    sku
                    price
                    compareAtPrice
                    inventoryQuantity
                    selectedOptions {
                      name
                      value
                    }
                    inventoryItem {
                      id
                      sku
                      tracked
                      inventoryLevels(first: 1) {
                        edges {
                          node {
                            id
                            quantities(names: ["available"]) {
                              name
                              quantity
                            }
                            location {
                              id
                              name
                              isActive
                            }
                          }
                        }
                      }
                    }
                    barcode
                    taxable
                    position
                    image {
                      id
                      url
                      altText
                      width
                      height
                    }
                  }
                }
              }
              metafields(first: 100) {
                edges {
                  node {
                    id
                    namespace
                    key
                    value
                    type
                  }
                }
              }
              images(first: 250) {
                edges {
                  node {
                    id
                    url
                    altText
                    width
                    height
                  }
                }
              }
              featuredImage {
                id
                url
                altText
                width
                height
              }
            }
          }
        }
      }
    `;
  }

  async deleteProductImage(productId, imageId) {
    try {
      console.log('Deleting product image:', { productId, imageId });

      // Format the IDs if needed (supports both ProductImage and MediaImage GIDs for API 2025-10 compatibility)
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;
      // For new API versions, use MediaImage format; for existing GIDs, keep as-is
      const formattedImageId = imageId.includes('gid://') ? imageId : `gid://shopify/MediaImage/${imageId}`;

      // First, get the product to find all current images
      const productQuery = `
        query getProduct($id: ID!) {
          product(id: $id) {
            id
            media(first: 250) {
              edges {
                node {
                  id
                  mediaContentType
                  alt
                  ... on MediaImage {
                    image {
                      originalSrc
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const productResult = await this.makeGraphQLRequest(productQuery, { id: formattedProductId });

      if (!productResult || !productResult.product) {
        throw new Error('Failed to retrieve product');
      }

      // Get all media IDs except the one we want to delete
      const currentMedia = productResult.product.media.edges
        .filter(edge => edge.node.id !== formattedImageId)
        .map(edge => ({
          id: edge.node.id,
          alt: edge.node.alt || '',
          mediaContentType: edge.node.mediaContentType
        }));

      // Create a mutation to update the product with the filtered media list
      const mutation = `
        mutation productUpdate($input: ProductInput!) {
          productUpdate(input: $input) {
            product {
              id
              media(first: 10) {
                edges {
                  node {
                    id
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      // Prepare the variables for the mutation
      const variables = {
        input: {
          id: formattedProductId,
          media: currentMedia
        }
      };

      const result = await this.makeGraphQLRequest(mutation, variables);

      if (result?.productUpdate?.userErrors?.length > 0) {
        const errors = result.productUpdate.userErrors;
        throw new Error(`Failed to delete product image: ${errors.map(e => e.message).join(', ')}`);
      }

      // Get the newly created image (should be the last one)
      const newImageNode = result?.productUpdate?.product?.images?.edges?.[0]?.node;

      if (!newImageNode) {
        throw new Error('Failed to retrieve the newly created image');
      }

      return {
        success: true,
        data: result.productUpdate.product
      };
    } catch (error) {
      console.error('Error deleting product image:', error);
      throw error;
    }
  }

  async getProductImages(productId) {
    try {
      const query = `
        query getProductImages($productId: ID!) {
          product(id: $productId) {
            images(first: 50) {
              edges {
                node {
                  id
                  src
                  altText
                  position
                  width
                  height
                }
              }
            }
          }
        }
      `;

      // Ensure productId is properly formatted
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;

      const variables = {
        productId: formattedProductId
      };

      console.log('Fetching product images with variables:', variables);
      const result = await this.makeGraphQLRequest(query, variables);

      if (!result?.product?.images?.edges) {
        return [];
      }

      return result.product.images.edges.map(edge => edge.node);
    } catch (error) {
      console.error('Error fetching product images:', error);
      return [];
    }
  }

  async createProductImage(productId, imageData) {
    try {
      // Updated to use productUpdate mutation for 2025-01 API version
      const mutation = `
        mutation productUpdate($input: ProductInput!) {
          productUpdate(input: $input) {
            product {
              id
              images(last: 1) {
                edges {
                  node {
                    id
                    src
                    altText
                    width
                    height
                  }
                }
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      // Format product ID if needed
      const graphqlProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;

      // First, get the current product images
      const productImages = await this.getProductImages(graphqlProductId);

      // Create a new image object
      const newImage = {
        src: imageData.src,
        altText: imageData.alt || '',
        position: imageData.position || (productImages.length + 1)
      };

      // Add the new image to the existing images
      const updatedImages = [...productImages.map(img => ({
        id: img.id,
        src: img.src,
        altText: img.altText || '',
        position: img.position || 1
      })), newImage];

      // Create variables for the mutation
      const variables = {
        input: {
          id: graphqlProductId,
          images: updatedImages
        }
      };

      const result = await this.makeGraphQLRequest(mutation, variables);

      if (result?.productUpdate?.userErrors?.length > 0) {
        const errors = result.productUpdate.userErrors;
        throw new Error(`Failed to create image: ${errors.map(e => e.message).join(', ')}`);
      }

      // Get the newly created image (should be the last one)
      const newImageNode = result?.productUpdate?.product?.images?.edges?.[0]?.node;

      if (!newImageNode) {
        throw new Error('Failed to retrieve the newly created image');
      }

      return {
        success: true,
        image: newImageNode
      };
    } catch (error) {
      console.error('Error creating product image via GraphQL:', error);
      throw error;
    }
  }

  async updateProductImage(productId, originalImage, processedImage) {
    try {
      console.log('Updating product image with:', { productId, originalImage, processedImage });

      // Ensure we have a valid product ID
      if (!productId) {
        throw new Error('Product ID is required for updating images');
      }

      // Format product ID if needed
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;

      // Initialize the batch processing collections if they don't exist
      if (!this.constructor._batchImageUploads) {
        this.constructor._batchImageUploads = new Map();
      }

      // Get or create the batch for this product
      if (!this.constructor._batchImageUploads.has(formattedProductId)) {
        this.constructor._batchImageUploads.set(formattedProductId, {
          images: [],
          variantMap: {}
        });
      }

      const batch = this.constructor._batchImageUploads.get(formattedProductId);

      // Add this image to the batch
      const position = batch.images.length + 1;
      batch.images.push({
        alt: originalImage.alt || '',
        src: processedImage.processedUrl || processedImage.url,
        originalSrc: originalImage.src || originalImage.url
      });

      // If this image has variant associations, add them to the map
      if (originalImage.variant_ids && originalImage.variant_ids.length > 0) {
        batch.variantMap[position] = originalImage.variant_ids;
        console.log(`Added variant associations for position ${position} in batch`);
      }

      console.log(`Added image to batch for product ${formattedProductId}, position ${position}`);

      // For now, just return success - actual processing will happen in bulk
      return {
        success: true,
        batchSize: batch.images.length
      };
    } catch (error) {
      console.error('Error adding image to batch:', error);
      throw error;
    }
  }

  /**
   * Process all batched images for a product
   * @param {string} productId - The ID of the product
   * @returns {Promise<object>} Result of the batch processing
   */
  async processBatchedImages(productId) {
    try {
      // Format product ID if needed
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;

      // Check if we have any batched images for this product
      if (!this.constructor._batchImageUploads || !this.constructor._batchImageUploads.has(formattedProductId)) {
        console.log(`No batched images found for product ${formattedProductId}`);
        return { success: true, processed: 0 };
      }

      const batch = this.constructor._batchImageUploads.get(formattedProductId);
      console.log(`Processing batch of ${batch.images.length} images for product ${formattedProductId}`);

      // Process the batch using the bulk method
      const result = await this.processProductImages(
        formattedProductId,
        batch.images,
        batch.variantMap,
        true // Replace existing images
      );

      // Clear the batch after processing
      this.constructor._batchImageUploads.delete(formattedProductId);

      return result;
    } catch (error) {
      console.error('Error processing batched images:', error);
      throw error;
    }
  }

  /**
   * Check if a product has any batched images and process them if needed
   * This method should be called after all images for a product have been added to the batch
   * @param {string} productId - The ID of the product
   * @param {string} shop - The shop domain
   * @param {string} accessToken - The shop access token
   * @returns {Promise<boolean>} True if batch was processed, false if no batch was found
   */
  static async triggerBatchProcessing(productId, shop, accessToken) {
    try {
      const service = new ShopifyBulkOperationsService(shop, accessToken);
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;

      // Check if we have any batched images for this product
      if (!service.constructor._batchImageUploads || !service.constructor._batchImageUploads.has(formattedProductId)) {
        console.log(`No batched images to process for product ${formattedProductId}`);
        return false;
      }

      const batchSize = service.constructor._batchImageUploads.get(formattedProductId).images.length;
      console.log(`Triggering batch processing for ${batchSize} images for product ${formattedProductId}`);

      // Process the batch
      const result = await service.processBatchedImages(formattedProductId);
      console.log(`Batch processing completed for product ${formattedProductId}:`, result);

      return result;
    } catch (error) {
      console.error('Error triggering batch processing:', error);
      return null;
    }
  }

  async associateImageWithVariants(productId, mediaId, variantIds) {
    try {
      // Format the variant IDs if needed
      const formattedVariantIds = variantIds.map(id => {
        return id.includes('gid://') ? id : `gid://shopify/ProductVariant/${id}`;
      });

      // Format the media ID if needed
      const formattedMediaId = mediaId.includes('gid://') ? mediaId : `gid://shopify/MediaImage/${mediaId}`;

      console.log(`Associating media ${formattedMediaId} with ${formattedVariantIds.length} variants`);

      // In Shopify API 2025-01, we need to use productVariantsBulkUpdate instead of productVariantUpdate
      const variantUpdateMutation = `
        mutation productVariantsBulkUpdate($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
          productVariantsBulkUpdate(productId: $productId, variants: $variants) {
            productVariants {
              id
              media(first: 250) {
                edges {
                  node {
                    id
                  }
                }
              }
            }
            userErrors {
              field
              message
              code
            }
          }
        }
      `;

      // Prepare the variables for the mutation
      const variants = formattedVariantIds.map(variantId => ({
        id: variantId,
        mediaId: formattedMediaId // Use mediaId (singular) as per Shopify API docs
      }));

      const variantUpdateVariables = {
        productId: productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`,
        variants: variants
      };

      console.log(`Updating ${variants.length} variants with image ${formattedMediaId}`);
      console.log('Mutation variables:', JSON.stringify(variantUpdateVariables, null, 2));

      // Execute the mutation
      const variantUpdateResult = await this.makeGraphQLRequest(variantUpdateMutation, variantUpdateVariables);

      if (variantUpdateResult?.productVariantsBulkUpdate?.userErrors?.length > 0) {
        const errors = variantUpdateResult.productVariantsBulkUpdate.userErrors;
        console.error(`Error updating variants with image:`, errors);

        // Provide more detailed error information
        const errorDetails = errors.map(err => `Field: ${err.field || 'unknown'}, Message: ${err.message}, Code: ${err.code || 'unknown'}`).join('\n');
        console.error(`Detailed error information:\n${errorDetails}`);

        // Check for specific error types and provide guidance
        const invalidFieldError = errors.find(err => err.message.includes('Field is not defined'));
        if (invalidFieldError) {
          console.error('This appears to be an API schema error. The field name might have changed in the Shopify API.');
          console.error('Please check the latest Shopify API documentation for the correct field name.');
        }

        // Continue even if there are errors
      } else {
        console.log(`Successfully updated variants with image ${formattedMediaId}`);

        // Log the response for debugging
        if (variantUpdateResult?.productVariantsBulkUpdate?.productVariants) {
          const updatedVariants = variantUpdateResult.productVariantsBulkUpdate.productVariants;
          console.log(`Updated ${updatedVariants.length} variants with new media associations`);
        }
      }

      // Note: Due to API limitations, we can't directly verify the media-variant associations
      // The user may need to manually check or use the Shopify Admin UI

      return true;
    } catch (error) {
      console.error('Error associating image with variants:', error);
      throw error;
    }
  }

  async getProductImageUpdateStatus(taskId) {
    // This is a simplified version since we're not actually using a task system anymore
    // We're just returning a completed status immediately
    return {
      data: {
        status: 'completed',
        progress: 100,
        message: 'Image update completed successfully'
      }
    };
  }

  static async deleteProductImage(shop, accessToken, productId, imageId) {
    const service = new ShopifyBulkOperationsService(shop, accessToken);
    return service.deleteProductImage(productId, imageId);
  }

  static async createProductImage(shop, accessToken, productId, imageData) {
    const service = new ShopifyBulkOperationsService(shop, accessToken);
    return service.createProductImage(productId, imageData);
  }

  static async updateProductImage(shop, accessToken, productId, originalImage, processedImage) {
    const service = new ShopifyBulkOperationsService(shop, accessToken);
    return service.updateProductImage(productId, originalImage, processedImage);
  }

  static async getProductImageUpdateStatus(shop, accessToken, taskId) {
    const service = new ShopifyBulkOperationsService(shop, accessToken);
    return service.getProductImageUpdateStatus(taskId);
  }

  /**
   * Deletes specific media items from a product
   * @param {string} productId - The ID of the product
   * @param {Array} mediaIds - Array of media IDs to delete
   * @returns {Promise<object>} Result of the deletion operation
   */
  async deleteProductMedia(productId, mediaIds) {
    try {
      console.log('Deleting product media:', { productId, mediaIds });

      // Ensure we have a valid product ID and media IDs
      if (!productId) {
        throw new Error('Product ID is required for deleting media');
      }

      if (!mediaIds || !Array.isArray(mediaIds) || mediaIds.length === 0) {
        console.warn('No media IDs provided for deletion');
        return { success: true, deletedCount: 0 };
      }

      // Format product ID if needed
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;

      // Format media IDs if needed
      const formattedMediaIds = mediaIds.map(id => {
        return id.includes('gid://') ? id : `gid://shopify/Media/${id}`;
      });

      console.log(`Deleting ${formattedMediaIds.length} media items from product ${formattedProductId}`);

      // Create the mutation for deleting media
      const deleteMediaMutation = `
        mutation productDeleteMedia($productId: ID!, $mediaIds: [ID!]!) {
          productDeleteMedia(productId: $productId, mediaIds: $mediaIds) {
            deletedMediaIds
            userErrors {
              field
              message
            }
          }
        }
      `;

      const deleteMediaVariables = {
        productId: formattedProductId,
        mediaIds: formattedMediaIds
      };

      // Execute the mutation
      const deleteMediaResult = await this.makeGraphQLRequest(deleteMediaMutation, deleteMediaVariables);

      // Check for errors
      if (deleteMediaResult?.productDeleteMedia?.userErrors?.length > 0) {
        const errors = deleteMediaResult.productDeleteMedia.userErrors;
        console.error('Error deleting product media:', errors);
        throw new Error(`Failed to delete media: ${errors.map(e => e.message).join(', ')}`);
      }

      // Get the IDs of the deleted media
      const deletedMediaIds = deleteMediaResult?.productDeleteMedia?.deletedMediaIds || [];

      console.log(`Successfully deleted ${deletedMediaIds.length} media items`);

      return {
        success: true,
        deletedCount: deletedMediaIds.length,
        deletedMediaIds
      };
    } catch (error) {
      console.error('Error deleting product media:', error);
      throw error;
    }
  }

  /**
   * Deletes multiple media items using the fileDelete mutation
   * @param {Array<string>} mediaIds - Array of media IDs to delete (format: "gid://shopify/MediaImage/123456789")
   * @returns {Promise<object>} Result of the deletion operation
   */
  async deleteMediaItems(mediaIds) {
    try {
      if (!mediaIds || !Array.isArray(mediaIds) || mediaIds.length === 0) {
        console.log('No media IDs provided for deletion');
        return { success: true, deletedCount: 0 };
      }

      console.log(`Deleting ${mediaIds.length} media items`);

      // Format media IDs if needed
      const formattedMediaIds = mediaIds.map(id => {
        return id.includes('gid://') ? id : `gid://shopify/MediaImage/${id}`;
      });

      const deleteMediaMutation = `
        mutation fileDelete($input: [ID!]!) {
          fileDelete(fileIds: $input) {
            deletedFileIds
            userErrors {
              field
              message
            }
          }
        }
      `;

      const deleteMediaVariables = {
        input: formattedMediaIds
      };

      const result = await this.makeGraphQLRequest(deleteMediaMutation, deleteMediaVariables);

      if (result?.fileDelete?.userErrors?.length > 0) {
        const errors = result.fileDelete.userErrors;
        console.error('Error deleting media:', errors);
        throw new Error(`Failed to delete media: ${errors.map(e => e.message).join(', ')}`);
      }

      const deletedIds = result?.fileDelete?.deletedFileIds || [];
      console.log(`Successfully deleted ${deletedIds.length} media items`);

      return {
        success: true,
        deletedCount: deletedIds.length,
        deletedIds
      };
    } catch (error) {
      console.error('Error deleting media items:', error);
      throw error;
    }
  }

  /**
   * Static method for deleting media items
   * @param {Array<string>} mediaIds - Array of media IDs to delete
   * @returns {Promise<object>} Result of the deletion operation
   */
  static async deleteMediaItems(mediaIds) {
    const service = new ShopifyBulkOperationsService();
    return service.deleteMediaItems(mediaIds);
  }

  async processProductImages(productId, imagesToUpload = [], imageVariantMap = {}, replaceExisting = true) {
    try {
      console.log('Processing product images:', {
        productId,
        uploadCount: imagesToUpload.length,
        associationCount: Object.keys(imageVariantMap).length,
        replaceExisting
      });

      if (!imagesToUpload || imagesToUpload.length === 0) {
        console.log('No images to upload');
        return { success: true, uploadedCount: 0 };
      }

      // Format product ID if needed
      const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;

      // First, get existing media IDs if we need to replace them
      let existingMediaIds = [];
      let existingMediaCdnUrls = []; // CDN URLs of existing images for description replacement
      if (replaceExisting) {
        try {
          // Query to get existing IMAGES only (not videos) to preserve videos
          // Also fetch CDN URLs so we can update them in the description later
          const getProductMediaQuery = `
            query GetProductMedia($productId: ID!) {
              product(id: $productId) {
                media(first: 250) {
                  edges {
                    node {
                      id
                      mediaContentType
                      ... on MediaImage {
                        image {
                          url
                        }
                      }
                    }
                  }
                }
              }
            }
          `;

          const getMediaVariables = {
            productId: formattedProductId
          };

          const mediaResult = await this.makeGraphQLRequest(getProductMediaQuery, getMediaVariables);

          if (mediaResult?.product?.media?.edges) {
            // Filter to only get IMAGE media types, preserve videos and other media
            const imageEdges = mediaResult.product.media.edges
              .filter(edge => edge.node.mediaContentType === 'IMAGE');
            existingMediaIds = imageEdges.map(edge => edge.node.id);
            // Capture CDN URLs of existing images (in order) for description URL replacement
            existingMediaCdnUrls = imageEdges.map(edge => edge.node?.image?.url || null).filter(Boolean);
            const totalMedia = mediaResult.product.media.edges.length;
            const videoCount = totalMedia - existingMediaIds.length;
            console.log(`Found ${existingMediaIds.length} existing images to replace (preserving ${videoCount} videos and other media)`);
            console.log(`📝 Captured ${existingMediaCdnUrls.length} existing CDN URLs for description update`);
          }
        } catch (error) {
          console.warn('Error fetching existing media, will continue with upload:', error);
        }
      }

      // Step 1: Upload all images in a single operation
      console.log(`Uploading ${imagesToUpload.length} images for product ${formattedProductId}`);

      // Create the media input from the image data, preserving order
      const mediaInput = imagesToUpload.map((img, index) => ({
        alt: img.alt || '', // Ensure alt text is preserved
        mediaContentType: 'IMAGE',
        originalSource: img.src || img.url
      }));

      // Store the original positions and metadata for later reference
      const originalPositions = imagesToUpload.map((img, index) => ({
        position: img.position || index + 1, // Use the image's position if available, otherwise use index
        alt: img.alt || '',
        variant_ids: img.variant_ids || [] // Store variant IDs for later association
      }));

      // Create the mutation for creating media
      const createMediaMutation = `
        mutation productCreateMedia($productId: ID!, $media: [CreateMediaInput!]!) {
          productCreateMedia(productId: $productId, media: $media) {
            media {
              ... on MediaImage {
                id
                image {
                  id
                  url
                  altText
                }
              }
            }
            mediaUserErrors {
              field
              message
            }
          }
        }
      `;

      const createMediaVariables = {
        productId: formattedProductId,
        media: mediaInput
      };

      console.log('Sending productCreateMedia mutation with variables:', createMediaVariables);

      // Execute the mutation
      const createMediaResult = await this.makeGraphQLRequest(createMediaMutation, createMediaVariables);

      // Check for errors
      if (createMediaResult?.productCreateMedia?.mediaUserErrors?.length > 0) {
        const errors = createMediaResult.productCreateMedia.mediaUserErrors;
        console.error('Error creating product media:', errors);
        throw new Error(`Failed to create media: ${errors.map(e => e.message).join(', ')}`);
      }

      // Get the uploaded media
      const uploadedMedia = createMediaResult?.productCreateMedia?.media || [];
      console.log(`Successfully uploaded ${uploadedMedia.length} media items`);

      // Step 2: Associate images with variants if needed
      const variantUpdates = [];
      const processedVariants = new Set();

      // Map original image positions to new media IDs
      const positionToMediaIdMap = {};
      uploadedMedia.forEach((media, index) => {
        // Use the index + 1 to match the original positions we stored earlier
        positionToMediaIdMap[index + 1] = media.id;
        console.log(`Mapped position ${index + 1} to media ID ${media.id}`);
      });

      // Check if we have any variant associations in the original images
      let hasVariantAssociations = false;
      originalPositions.forEach(originalImage => {
        if (originalImage.variant_ids && originalImage.variant_ids.length > 0) {
          hasVariantAssociations = true;
        }
      });

      // If we have no variant associations in the original images, check if they might be in the metadata
      if (!hasVariantAssociations) {
        console.log('No variant associations found in original images, checking metadata...');
        imagesToUpload.forEach(img => {
          // Check if variant_ids exists in the metadata
          if (img.metadata?.shopifyMetadata?.variant_ids && img.metadata.shopifyMetadata.variant_ids.length > 0) {
            console.log(`Found variant_ids in metadata for image: ${img.metadata.shopifyMetadata.variant_ids.join(', ')}`);
            hasVariantAssociations = true;

            // Add these to the originalPositions array to be processed later
            const position = img.metadata.shopifyMetadata.position;
            if (position) {
              // Find if we already have this position in originalPositions
              const existingIndex = originalPositions.findIndex(img => img.position === position);
              if (existingIndex >= 0) {
                // Update the existing entry with variant_ids
                originalPositions[existingIndex].variant_ids = img.metadata.shopifyMetadata.variant_ids;
                console.log(`Updated variant_ids for position ${position} in originalPositions`);
              }
            }
          }
        });
      }

      // Process variant associations from the imageVariantMap
      Object.entries(imageVariantMap).forEach(([imageKey, variantIds]) => {
        if (!variantIds || !Array.isArray(variantIds) || variantIds.length === 0) {
          return;
        }

        // Improved position extraction from imageKey
        let position;
        if (typeof imageKey === 'number') {
          // If imageKey is already a number, use it directly
          position = imageKey;
        } else if (typeof imageKey === 'string') {
          // Try to extract position from string
          // First check if it's a simple numeric string
          if (/^\d+$/.test(imageKey)) {
            position = parseInt(imageKey);
          } else if (imageKey.includes('gid://')) {
            // If it's a Shopify GID, log it and try to find by ID directly
            console.log(`Image key is a Shopify GID: ${imageKey}`);
            // For GIDs, we might need to look up by ID instead of position
            // This is a fallback that might not always work
            const matchingMedia = uploadedMedia.find(media =>
              media.id === imageKey ||
              (media.image && media.image.id === imageKey)
            );
            if (matchingMedia) {
              // If we found a direct match, use its ID
              const mediaId = matchingMedia.id;
              // Associate variants with this media
              variantIds.forEach(variantId => {
                if (!processedVariants.has(variantId)) {
                  processedVariants.add(variantId);
                  variantUpdates.push({
                    id: variantId,
                    mediaId: mediaId // Use mediaId (singular) as per Shopify API docs
                  });
                }
              });
              return; // Skip the rest of this iteration
            } else {
              // If no direct match, try to extract a position from the ID
              const positionMatch = imageKey.match(/(\d+)$/);
              position = positionMatch ? parseInt(positionMatch[1]) : null;
            }
          } else {
            // Last resort: try to extract any number from the string
            const numberMatch = imageKey.match(/\d+/);
            position = numberMatch ? parseInt(numberMatch[0]) : null;
          }
        }

        // If we couldn't determine a position, log and skip
        if (!position) {
          console.warn(`Could not determine position from image key: ${imageKey}`);
          return;
        }

        const mediaId = positionToMediaIdMap[position];

        if (!mediaId) {
          console.warn(`No media found for position ${position}`);
          return;
        }

        // Add each variant to the update list if not already processed
        variantIds.forEach(variantId => {
          if (!processedVariants.has(variantId)) {
            processedVariants.add(variantId);
            variantUpdates.push({
              id: variantId,
              mediaId: mediaId // Use mediaId (singular) as per Shopify API docs
            });
          }
        });
      });

      // Also process variant associations from the original images as a fallback
      // This ensures we preserve variant associations even if not explicitly provided in imageVariantMap
      originalPositions.forEach((originalImage, index) => {
        if (!originalImage.variant_ids || originalImage.variant_ids.length === 0) {
          return;
        }

        const position = originalImage.position;
        const mediaId = positionToMediaIdMap[position];

        if (!mediaId) {
          console.warn(`No media found for position ${position} from original image`);
          return;
        }

        // Add each variant to the update list if not already processed
        originalImage.variant_ids.forEach(variantId => {
          if (!processedVariants.has(variantId)) {
            processedVariants.add(variantId);
            variantUpdates.push({
              id: variantId,
              mediaId: mediaId // Use mediaId (singular) as per Shopify API docs
            });
            console.log(`Added variant association from original image: variant ${variantId} -> media ${mediaId}`);
          }
        });
      });

      // If we have variant updates, perform them
      if (variantUpdates.length > 0) {
        console.log(`Updating ${variantUpdates.length} variants with image associations`);

        const updateVariantsMutation = `
          mutation productVariantsBulkUpdate($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
            productVariantsBulkUpdate(productId: $productId, variants: $variants) {
              productVariants {
                id
                media(first: 250) {
                  edges {
                    node {
                      id
                    }
                  }
                }
              }
              userErrors {
                field
                message
              }
            }
          }
        `;

        const updateVariantsVariables = {
          productId: formattedProductId,
          variants: variantUpdates
        };

        const updateVariantsResult = await this.makeGraphQLRequest(updateVariantsMutation, updateVariantsVariables);

        if (updateVariantsResult?.productVariantsBulkUpdate?.userErrors?.length > 0) {
          const errors = updateVariantsResult.productVariantsBulkUpdate.userErrors;
          console.error('Error updating variants with images:', errors);
          throw new Error(`Failed to update variants: ${errors.map(e => e.message).join(', ')}`);
        }

        console.log('Successfully updated variants with images');
      }

      // Step 3: Unlink old media if replaceExisting is true and we have existing media
      if (replaceExisting && existingMediaIds.length > 0) {
        try {
          console.log(`Unlinking ${existingMediaIds.length} old media items from product ${formattedProductId}`);

          // Unlink old media using the fileUpdate mutation instead of deleting them
          const unlinkResult = await this.unlinkMediaFromProduct(productId, existingMediaIds);

          console.log(`Successfully unlinked ${unlinkResult.unlinkedCount} old media items from product ${formattedProductId}`);
        } catch (unlinkError) {
          // Log the error but don't fail the entire operation
          console.error('Error unlinking old media:', unlinkError);
        }
      }

      // Step 4: Update image URLs in product description
      let descUpdateResult = { newCdnUrls: [] };
      if (replaceExisting && uploadedMedia.length > 0) {
        try {
          console.log(`Updating image URLs in product description for product ${formattedProductId}`);
          descUpdateResult = await this.updateProductDescriptionImages(formattedProductId, imagesToUpload, uploadedMedia, existingMediaCdnUrls);
        } catch (descError) {
          console.error("Error updating product description URLs:", descError);
        }
      }

      // Step 5: Fetch true final file sizes from Shopify CDN
      const finalCdnSizes = [];
      const newUrls = descUpdateResult?.newCdnUrls || [];
      for (const url of newUrls) {
        if (!url) {
          finalCdnSizes.push(null);
          continue;
        }
        try {
          const response = await fetch(url, { method: 'HEAD' });
          const size = response.headers.get('content-length');
          finalCdnSizes.push(size ? parseInt(size, 10) : null);
          console.log(`Fetched CDN size for ${url}: ${size} bytes`);
        } catch (error) {
          console.warn(`Could not fetch HEAD size for ${url}:`, error);
          finalCdnSizes.push(null);
        }
      }

      return {
        success: true,
        uploadedCount: uploadedMedia.length,
        updatedVariantsCount: variantUpdates.length,
        unlinkedMediaCount: replaceExisting ? existingMediaIds.length : 0,
        finalCdnSizes
      };
    } catch (error) {
      console.error('Error processing product images:', error);
      throw error;
    }
  }

  /**
   * Updates product description by replacing old image URLs with new uploaded media URLs.
   * Four strategies (applied in order):
   * 1. CDN position mapping: existingMediaCdnUrls[i] → new uploaded CDN URL[i] (most reliable)
   * 2. Direct mapping: use uploadedMedia IDs + imagesToUpload.originalSrc
   * 3. Placeholder match: data-image-placeholder="{{IMAGE_X}}" → X-th product image
   * 4. Broken placeholder scan: find any data-image-placeholder with broken HTML and replace src
   * @param {string} productId
   * @param {Array} imagesToUpload - batch images with originalSrc
   * @param {Array} uploadedMedia - mutation response with media IDs
   * @param {Array} existingMediaCdnUrls - CDN URLs of old images captured before unlink
   */
  async updateProductDescriptionImages(productId, imagesToUpload, uploadedMedia, existingMediaCdnUrls = []) {
    try {
      if (!imagesToUpload || imagesToUpload.length === 0) return { newCdnUrls: [] };

      console.log(`📝 [DescUpdate] Starting description image URL update for product ${productId}`);
      console.log(`📝 [DescUpdate] ${imagesToUpload.length} images uploaded, ${uploadedMedia?.length || 0} media returned, ${existingMediaCdnUrls.length} old CDN URLs captured`);

      // Build a mapping of uploaded media IDs for targeted polling
      const uploadedMediaIds = (uploadedMedia || [])
        .map(m => m?.id)
        .filter(Boolean);

      // Log old CDN URLs for debugging
      for (let i = 0; i < existingMediaCdnUrls.length; i++) {
        const url = existingMediaCdnUrls[i];
        console.log(`📝 [DescUpdate] Old CDN[${i}]: ${url ? url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('/') + 60) : 'N/A'}`);
      }

      // Query product description and poll for new media URLs
      const mediaQuery = `
        query getProductDesc($id: ID!) {
          product(id: $id) {
            descriptionHtml
            media(first: 250) {
              edges {
                node {
                  ... on MediaImage {
                    id
                    status
                    image { url }
                  }
                }
              }
            }
          }
        }
      `;

      let descriptionHtml = null;
      let allMediaNodes = [];
      const maxAttempts = 8;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const waitMs = attempt === 1 ? 2000 : 3000;
        console.log(`📝 [DescUpdate] Waiting ${waitMs}ms for Shopify to process images (attempt ${attempt}/${maxAttempts})...`);
        await new Promise(resolve => setTimeout(resolve, waitMs));

        const result = await this.makeGraphQLRequest(mediaQuery, { id: productId });
        descriptionHtml = result?.product?.descriptionHtml;

        if (!descriptionHtml) {
          console.log('📝 [DescUpdate] No description HTML found, skipping');
          return;
        }

        allMediaNodes = (result?.product?.media?.edges || [])
          .filter(edge => edge.node?.id)
          .map(edge => ({
            id: edge.node.id,
            url: edge.node?.image?.url || null,
            status: edge.node?.status
          }));

        const uploadedWithUrls = allMediaNodes
          .filter(m => uploadedMediaIds.includes(m.id) && m.url)
          .length;

        console.log(`📝 [DescUpdate] Attempt ${attempt}: ${uploadedWithUrls}/${uploadedMediaIds.length} uploaded media URLs ready (${allMediaNodes.length} total media on product)`);

        if (uploadedWithUrls >= uploadedMediaIds.length || uploadedWithUrls >= uploadedMediaIds.length * 0.8) {
          console.log('📝 [DescUpdate] Sufficient uploaded media URLs ready');
          break;
        }

        if (attempt === maxAttempts) {
          console.log(`📝 [DescUpdate] Proceeding with ${uploadedWithUrls} URLs after ${maxAttempts} attempts`);
        }
      }

      // Build media ID → fresh CDN URL lookup
      const mediaIdToUrl = new Map();
      for (const node of allMediaNodes) {
        if (node.id && node.url) {
          mediaIdToUrl.set(node.id, node.url);
        }
      }

      // Get new CDN URLs in upload order
      const newCdnUrls = uploadedMediaIds.map(id => mediaIdToUrl.get(id) || null);

      // Helper: Strip UUID suffix from filenames
      // Shopify appends UUIDs on upload: "image_a6edd25d-a410-4035-90fd-a8bbd5b99325.jpg"
      // but the description has the original: "image.jpg"
      const stripUuidSuffix = (filename) => {
        return filename.replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\.[a-zA-Z]+)$/i, '$1');
      };

      let hasChanges = false;

      // ── STRATEGY 1: CDN position mapping (PRIMARY - most reliable) ──
      // existingMediaCdnUrls[i] is the ACTUAL Shopify CDN URL of the i-th old image
      // newCdnUrls[i] is the fresh CDN URL of the i-th newly uploaded image
      if (existingMediaCdnUrls.length > 0) {
        console.log(`📝 [DescUpdate] Strategy 1: CDN position mapping with ${existingMediaCdnUrls.length} old → ${newCdnUrls.filter(Boolean).length} new URLs...`);

        const count = Math.min(existingMediaCdnUrls.length, newCdnUrls.length);
        for (let i = 0; i < count; i++) {
          const oldCdnUrl = existingMediaCdnUrls[i];
          const newCdnUrl = newCdnUrls[i];

          if (!oldCdnUrl || !newCdnUrl) continue;

          // Strip query params from old URL for matching
          const oldBase = oldCdnUrl.split('?')[0];

          if (descriptionHtml.includes(oldBase)) {
            console.log(`📝 [DescUpdate] ✓ [CDN] Found old CDN URL at position ${i} in description, replacing...`);
            const escaped = oldBase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // Match the URL with optional query params
            const urlRegex = new RegExp(escaped + '(\\?[^"\'\\s>]*)?', 'g');
            descriptionHtml = descriptionHtml.replace(urlRegex, newCdnUrl);
            hasChanges = true;
          } else {
            // Try matching by filename only (sometimes the full path differs slightly)
            const oldFilename = oldBase.substring(oldBase.lastIndexOf('/') + 1);
            // Also try with UUID suffix stripped (Shopify appends UUIDs on upload)
            const oldFilenameNoUuid = stripUuidSuffix(oldFilename);

            if (oldFilename && descriptionHtml.includes(oldFilename)) {
              console.log(`📝 [DescUpdate] ✓ [CDN-filename] Found old filename "${oldFilename}" in description, replacing...`);
              const escaped = oldFilename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const filenameRegex = new RegExp('(https://cdn\\.shopify\\.com/[^"\'\\s>]*?)' + escaped + '(\\?[^"\'\\s>]*)?', 'g');
              descriptionHtml = descriptionHtml.replace(filenameRegex, newCdnUrl);
              hasChanges = true;
            } else if (oldFilenameNoUuid !== oldFilename && descriptionHtml.includes(oldFilenameNoUuid)) {
              // Match by filename WITHOUT UUID suffix
              console.log(`📝 [DescUpdate] ✓ [CDN-noUUID] Found "${oldFilenameNoUuid}" (stripped UUID from "${oldFilename}") in description, replacing...`);
              const escaped = oldFilenameNoUuid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const filenameRegex = new RegExp('(https://cdn\\.shopify\\.com/[^"\'\\s>]*?)' + escaped + '(\\?[^"\'\\s>]*)?', 'g');
              descriptionHtml = descriptionHtml.replace(filenameRegex, newCdnUrl);
              hasChanges = true;
            }
          }
        }
      }

      // ── STRATEGY 2: imagesToUpload.originalSrc mapping (fallback) ──
      if (!hasChanges) {
        console.log(`📝 [DescUpdate] Strategy 2: Trying originalSrc matching as fallback...`);
        for (let i = 0; i < imagesToUpload.length; i++) {
          const oldSrc = imagesToUpload[i]?.originalSrc;
          const newSrc = newCdnUrls[i];
          if (!oldSrc || !newSrc) continue;

          const oldBase = oldSrc.split('?')[0];
          if (descriptionHtml.includes(oldBase)) {
            console.log(`📝 [DescUpdate] ✓ [originalSrc] Found match at index ${i}, replacing...`);
            const escaped = oldBase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const urlRegex = new RegExp(escaped + '(\\?[^"\'\\s>]*)?', 'g');
            descriptionHtml = descriptionHtml.replace(urlRegex, newSrc);
            hasChanges = true;
          }
        }
      }

      // ── STRATEGY 3: {{IMAGE_X}} placeholder replacement ──
      const placeholderRegex = /data-image-placeholder="\{\{IMAGE_(\d+)\}\}"/g;
      let placeholderMatch;
      const placeholdersFound = [];
      while ((placeholderMatch = placeholderRegex.exec(descriptionHtml)) !== null) {
        placeholdersFound.push({
          fullMatch: placeholderMatch[0],
          imageIndex: parseInt(placeholderMatch[1], 10)
        });
      }

      if (placeholdersFound.length > 0) {
        console.log(`📝 [DescUpdate] Strategy 3: Found ${placeholdersFound.length} {{IMAGE_X}} placeholders`);
        const allFreshUrls = allMediaNodes.map(m => m.url).filter(Boolean);

        for (const placeholder of placeholdersFound) {
          const mediaIndex = placeholder.imageIndex - 1;
          const targetUrl = allFreshUrls[mediaIndex];
          if (!targetUrl) continue;

          const escapedPlaceholder = placeholder.fullMatch.replace(/[{}]/g, '\\$&');
          const imgRegex1 = new RegExp(`(<img[^>]*src=")[^"]*("[^>]*${escapedPlaceholder}[^>]*>)`, 'gi');
          const imgRegex2 = new RegExp(`(<img[^>]*${escapedPlaceholder}[^>]*src=")[^"]*("[^>]*>)`, 'gi');

          const before = descriptionHtml;
          descriptionHtml = descriptionHtml.replace(imgRegex1, `$1${targetUrl}$2`);
          if (descriptionHtml === before) {
            descriptionHtml = descriptionHtml.replace(imgRegex2, `$1${targetUrl}$2`);
          }
          if (descriptionHtml !== before) {
            console.log(`📝 [DescUpdate] ✓ [Placeholder] Replaced IMAGE_${placeholder.imageIndex}`);
            hasChanges = true;
          }
        }
      }

      // ── STRATEGY 4: Broken placeholder / any img with data-image-placeholder ──
      // The AI sometimes generates broken placeholders like data-image-placeholder="&lt;img src="
      // Instead of the expected {{IMAGE_X}} format. Find ALL img tags with data-image-placeholder
      // and replace their src with the corresponding new CDN URL based on img tag order.
      // This runs independently as a catch-all, even if Strategy 1 already changed some URLs.
      if (existingMediaCdnUrls.length > 0 && newCdnUrls.filter(Boolean).length > 0) {
        console.log(`📝 [DescUpdate] Strategy 4: Scanning for broken/any data-image-placeholder img tags...`);

        // Find all <img> tags that have a data-image-placeholder attribute (any value)
        const imgTagRegex = /<img\s[^>]*data-image-placeholder[^>]*>/gi;
        const imgTags = [];
        let imgMatch;
        while ((imgMatch = imgTagRegex.exec(descriptionHtml)) !== null) {
          imgTags.push({
            fullTag: imgMatch[0],
            index: imgMatch.index
          });
        }

        if (imgTags.length > 0) {
          console.log(`📝 [DescUpdate] Strategy 4: Found ${imgTags.length} img tags with data-image-placeholder attr`);
          const allFreshUrls = allMediaNodes.map(m => m.url).filter(Boolean);

          // Map each img tag to the corresponding product image by order
          // The AI inserts images in order, so the 1st placeholder img = 1st product image, etc.
          // But we need to figure out WHICH image it represents. We'll try to find the position
          // by checking which existing CDN URL is in the src.
          for (let tagIdx = 0; tagIdx < imgTags.length; tagIdx++) {
            const tag = imgTags[tagIdx];
            const srcMatch = tag.fullTag.match(/src="([^"]*)"/);
            if (!srcMatch) continue;

            const currentSrc = srcMatch[1];
            const currentSrcBase = currentSrc.split('?')[0];

            // Check if this tag already has a valid placeholder
            const placeholderMatch = tag.fullTag.match(/data-image-placeholder="\{\{IMAGE_(\d+)\}\}"/);
            const hasValidPlaceholder = !!placeholderMatch;

            // Check if the tag has broken/duplicate attributes (sign of AI malformation)
            const isBrokenTag = !hasValidPlaceholder || 
              (tag.fullTag.match(/style="/g) || []).length > 1 ||
              (tag.fullTag.match(/loading="/g) || []).length > 1 ||
              tag.fullTag.includes('&lt;') || tag.fullTag.includes('&gt;');

            // Find which old CDN URL this matches
            let matchedNewUrl = null;
            let matchedPosition = -1;
            for (let i = 0; i < existingMediaCdnUrls.length; i++) {
              const oldCdn = existingMediaCdnUrls[i];
              if (!oldCdn) continue;
              const oldCdnBase = oldCdn.split('?')[0];
              const oldFilename = oldCdnBase.substring(oldCdnBase.lastIndexOf('/') + 1);
              const oldFilenameNoUuid = stripUuidSuffix(oldFilename);
              const currentSrcFilename = currentSrcBase.substring(currentSrcBase.lastIndexOf('/') + 1);
              const currentSrcFilenameNoUuid = stripUuidSuffix(currentSrcFilename);

              if (currentSrcBase === oldCdnBase || 
                  currentSrc.includes(oldFilename) ||
                  (oldFilenameNoUuid !== oldFilename && currentSrc.includes(oldFilenameNoUuid)) ||
                  (currentSrcFilenameNoUuid === oldFilenameNoUuid)) {
                matchedNewUrl = newCdnUrls[i];
                matchedPosition = i + 1;
                console.log(`📝 [DescUpdate] ✓ [BrokenPlaceholder] Matched img tag ${tagIdx} (${oldFilenameNoUuid}) → new CDN URL`);
                break;
              }
            }

            if (matchedNewUrl) {
              if (isBrokenTag) {
                // Rebuild the entire tag cleanly to fix duplicate/garbled attributes
                const altMatch = tag.fullTag.match(/alt="([^"]*)"/);
                const alt = altMatch ? altMatch[1] : '';
                const placeholderValue = hasValidPlaceholder 
                  ? `{{IMAGE_${placeholderMatch[1]}}}` 
                  : (matchedPosition > 0 ? `{{IMAGE_${matchedPosition}}}` : '');
                const dataPlaceholder = placeholderValue ? ` data-image-placeholder="${placeholderValue}"` : '';
                const cleanTag = `<img src="${matchedNewUrl}" alt="${alt}" class="product-description-image"${dataPlaceholder} style="max-width: 100%; height: auto;" loading="lazy">`;
                console.log(`📝 [DescUpdate] ✓ [CleanRebuild] Rebuilt broken img tag ${tagIdx} with clean attributes`);
                descriptionHtml = descriptionHtml.replace(tag.fullTag, cleanTag);
              } else {
                // Tag structure is valid, just replace the src
                const newTag = tag.fullTag.replace(/src="[^"]*"/, `src="${matchedNewUrl}"`);
                descriptionHtml = descriptionHtml.replace(tag.fullTag, newTag);
              }
              hasChanges = true;
            }
          }
        }
      }

      // ── SAVE ──
      if (hasChanges) {
        console.log(`📝 [DescUpdate] Saving updated description for product ${productId}...`);
        const mutation = `
          mutation productUpdateDesc($input: ProductInput!) {
            productUpdate(input: $input) {
              product { id }
              userErrors { field message }
            }
          }
        `;
        const res = await this.makeGraphQLRequest(mutation, {
          input: { id: productId, descriptionHtml }
        });

        if (res?.productUpdate?.userErrors?.length > 0) {
          console.error('📝 [DescUpdate] Failed to update description:', res.productUpdate.userErrors);
        } else {
          console.log(`📝 [DescUpdate] ✓ Successfully updated description image URLs for product ${productId}`);
        }
      } else {
        console.log(`📝 [DescUpdate] No matching image URLs found in description for product ${productId}`);
      }
      
      return { newCdnUrls };
    } catch (error) {
      console.error('📝 [DescUpdate] Error updating product description images:', error);
      return { newCdnUrls: [] };
    }
  }

  /**
   * Static method for processing product images
   * @param {string} productId - The ID of the product
   * @param {Array} imagesToUpload - Array of image data to upload
   * @param {Object} imageVariantMap - Mapping of image IDs to variant IDs for association
   * @param {boolean} replaceExisting - Whether to replace all existing images (default: true)
   * @returns {Promise<object>} Result of the image processing operation
   */
  static async processProductImages(productId, imagesToUpload = [], imageVariantMap = {}, replaceExisting = true) {
  const service = new ShopifyBulkOperationsService();
  return service.processProductImages(productId, imagesToUpload, imageVariantMap, replaceExisting);
}

  /**
   * Unlinks media items from a product using the fileUpdate mutation
   * @param {string} productId - The ID of the product to unlink media from
   * @param {Array<string>} mediaIds - Array of media IDs to unlink (format: "gid://shopify/MediaImage/123456789")
   * @returns {Promise<object>} Result of the unlinking operation
   */
  async unlinkMediaFromProduct(productId, mediaIds) {
  try {
    if (!mediaIds || !Array.isArray(mediaIds) || mediaIds.length === 0) {
      console.log('No media IDs provided for unlinking');
      return { success: true, unlinkedCount: 0 };
    }

    // Format product ID if needed
    const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;
console.log(`Unlinking ${mediaIds.length} media items from product ${formattedProductId}`);

// Format media IDs if needed
const formattedMediaIds = mediaIds.map(id => {
  return id.includes('gid://') ? id : `gid://shopify/MediaImage/${id}`;
});

// Prepare the input for fileUpdate mutation
const fileUpdateInputs = formattedMediaIds.map(mediaId => ({
  id: mediaId,
  referencesToRemove: [formattedProductId]
}));

const fileUpdateMutation = `
        mutation fileUpdate($files: [FileUpdateInput!]!) {
          fileUpdate(files: $files) {
            files {
              id
              alt
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

const fileUpdateVariables = {
  files: fileUpdateInputs
};

const result = await this.makeGraphQLRequest(fileUpdateMutation, fileUpdateVariables);

if (result?.fileUpdate?.userErrors?.length > 0) {
  const errors = result.fileUpdate.userErrors;
  console.error('Error unlinking media:', errors);
  throw new Error(`Failed to unlink media: ${errors.map(e => e.message).join(', ')}`);
}

const unlinkedFiles = result?.fileUpdate?.files || [];
console.log(`Successfully unlinked ${unlinkedFiles.length} media items from product ${formattedProductId}`);

return {
  success: true,
  unlinkedCount: unlinkedFiles.length,
  unlinkedFiles
};
  } catch (error) {
  console.error('Error unlinking media items:', error);
  throw error;
}
}

  /**
   * Static method for unlinking media items from a product
   * @param {string} productId - The ID of the product to unlink media from
   * @param {Array<string>} mediaIds - Array of media IDs to unlink
   * @returns {Promise<object>} Result of the unlinking operation
   */
  static async unlinkMediaFromProduct(productId, mediaIds) {
  const service = new ShopifyBulkOperationsService();
  return service.unlinkMediaFromProduct(productId, mediaIds);
}

  /**
   * Update a product with the provided data including option names and values
   * @param {string} productId - The ID of the product to update
   * @param {object} updateData - The data to update the product with
   * @returns {Promise<object>} Result of the update operation
   */
  async updateProduct(productId, updateData) {
  console.log('Updating product with localEdits:', { productId, updateData });

  try {
    // Format product ID if needed
    const formattedProductId = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId}`;

    // First, get the current product data to preserve existing option names
    let currentProduct = null;
    const hasOptionChanges = Object.keys(updateData).some(key => key.match(/^option\d+_name$/));

    if (hasOptionChanges) {
      try {
        const productQuery = `
            query getProduct($id: ID!) {
              product(id: $id) {
                id
                options {
                  id
                  name
                  position
                  values
                }
              }
            }
          `;

        const productResult = await this.makeGraphQLRequest(productQuery, {
          id: formattedProductId
        });

        currentProduct = productResult?.product;
        console.log('Current product options:', currentProduct?.options);
      } catch (error) {
        console.warn('Could not fetch current product options, proceeding without them:', error);
      }
    }

    // Build the product input for GraphQL
    const productInput = {
      id: formattedProductId
    };

    // Handle standard fields
    if (updateData.title !== undefined) productInput.title = updateData.title;
    if (updateData.descriptionHtml !== undefined) productInput.descriptionHtml = updateData.descriptionHtml;
    if (updateData.handle !== undefined) productInput.handle = updateData.handle;
    if (updateData.vendor !== undefined) productInput.vendor = updateData.vendor;
    if (updateData.productType !== undefined) productInput.productType = updateData.productType;
    if (updateData.product_type !== undefined) productInput.productType = updateData.product_type;
    if (updateData.tags !== undefined) productInput.tags = updateData.tags;

    // Handle option names - preserve existing options and merge with changes
    if (hasOptionChanges && currentProduct?.options) {
      const options = [];

      // Build complete options array with both existing and updated names
      const maxOptions = Math.max(currentProduct.options.length,
        Object.keys(updateData).filter(key => key.match(/^option\d+_name$/)).length);

      for (let i = 0; i < maxOptions; i++) {
        const position = i + 1;
        const currentOption = currentProduct.options.find(opt => opt.position === position);
        const optionKey = `option${position}_name`;

        let optionName = null;

        // Check if this option has been updated in localEdits
        if (updateData[optionKey] !== undefined) {
          optionName = updateData[optionKey];
        } else if (currentOption) {
          // Use existing option name if not being updated
          optionName = currentOption.name;
        }

        // Only add option if we have a name for it
        if (optionName && optionName.trim() !== '') {
          options.push({
            name: optionName,
            position: position
          });
        }
      }

      if (options.length > 0) {
        productInput.options = options;
        console.log('Setting complete product options (existing + changes):', options);
      }
    }

    console.log('Final productInput for GraphQL:', productInput);

    // Use GraphQL productUpdate mutation
    const mutation = `
        mutation productUpdate($input: ProductInput!) {
          productUpdate(input: $input) {
            product {
              id
              title
              handle
              vendor
              productType
              tags
              options {
                id
                name
                position
                values
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

    const result = await this.makeGraphQLRequest(mutation, {
      input: productInput
    });

    if (result?.productUpdate?.userErrors?.length > 0) {
      const errors = result.productUpdate.userErrors;
      console.error('GraphQL errors updating product:', errors);
      throw new Error(`Failed to update product: ${errors.map(e => e.message).join(', ')}`);
    }

    console.log('Successfully updated product via GraphQL');
    return result.productUpdate.product;
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
}
}

export default ShopifyBulkOperationsService;
